import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, ShoppingBag, Loader2, Sparkles } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { getUserWishlistV2 } from "../hooks/useWishlist";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import ProductGrid from "../components/_ResponsiveComponents/ProductCard";
import SEO from "../components/SEO/SEO";
import type { Product } from "../hooks/products";
import { transformRawProducts } from "../hooks/products/transformations";

const WishlistPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, userId } = useAuth();

  const [rawItems, setRawItems] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate("/registration");
      return;
    }
    fetchWishlist();
  }, [user, userId, navigate]);

  const fetchWishlist = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const response = await getUserWishlistV2({ user_id: userId });
      setRawItems(response?.data?.items ?? []);
    } catch (error) {
      console.error("Error fetching wishlist:", error);
      setRawItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Map raw wishlist items → Product type for ProductGrid
  const products: Product[] = useMemo(
    () => transformRawProducts(rawItems as any[]),
    [rawItems],
  );

  return (
    <>
      <SEO title="My Wishlist" description="Your saved perfumes and fragrances." noindex />
      <Header />
      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          {/* ── Page Title ── */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Heart className="w-5 h-5 text-primary fill-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-section-text leading-tight">
                My Wishlist
              </h1>
              {!loading && products.length > 0 && (
                <p className="text-sm text-section-muted mt-0.5">
                  {products.length} {products.length === 1 ? "item" : "items"}{" "}
                  saved
                </p>
              )}
            </div>
          </div>

          {/* ── States ── */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-32">
              <Loader2 className="w-10 h-10 animate-spin text-primary mb-4" />
              <p className="text-section-muted text-sm font-medium">
                Loading your wishlist…
              </p>
            </div>
          ) : products.length === 0 ? (
            /* ── Empty State ── */
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="relative mb-6">
                <div className="w-24 h-24 rounded-full bg-section-surface border border-section-border flex items-center justify-center">
                  <Heart className="w-10 h-10 text-section-muted" />
                </div>
                <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-accent/15 border border-accent/30 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-accent" />
                </div>
              </div>
              <h2 className="text-xl font-semibold text-section-text mb-2">
                Nothing here yet
              </h2>
              <p className="text-section-muted text-sm max-w-sm mb-8">
                Start browsing and tap the heart icon on products you love.
                They'll show up right here.
              </p>
              <button
                onClick={() => navigate("/products")}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl hover:opacity-90 transition-opacity font-semibold text-sm shadow-[0_4px_14px_rgba(201,169,110,0.2)] hover:shadow-[0_4px_14px_rgba(201,169,110,0.35)]"
              >
                <ShoppingBag className="w-4 h-4" />
                Browse Products
              </button>
            </div>
          ) : (
            /* ── Product Grid ── */
            <ProductGrid
              products={products}
              viewMode="grid"
              loading={false}
              isShowWishlist={true}
            />
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default WishlistPage;
